import { FC, useEffect, useState } from 'react';
import { Divider, Typography } from 'antd';
import { TbArrowRight } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { FEATURE_ADD_ON_CONFIG, isFeatureAddOnReleased } from '@/constants';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { OurDocsLink, PlatformAlert } from '@/shared';

import FeatureAddOnCard from './FeatureAddOnCard';

const FeatureAddOns: FC = (): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { hasMinimumPlan } = useEntitlements();

    const { token } = useAntDesignTheme();

    const isActivePaidPlan = hasMinimumPlan('plus');

    const featureAddOns = Object.values(FEATURE_ADD_ON_CONFIG).filter(({ key }) => isFeatureAddOnReleased(key));

    return (
        <div>
            <Typography.Title id='settings-account-plans-addons-section' level={4}>
                Add-Ons
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                View, upgrade, and manage your feature add-ons below.
            </Typography.Paragraph>
            <Divider style={{ margin: '1rem 0' }} />
            {!isActivePaidPlan && (
                <>
                    <PlatformAlert
                        styles={{ marginBottom: '1rem' }}
                        status='warning'
                        message={
                            <>
                                Add a{' '}
                                <Link to={'/settings/account/plans/seats'}>
                                    Seats Plan
                                    <TbArrowRight style={{ verticalAlign: '-0.175rem', marginLeft: '0.125rem' }} />
                                </Link>{' '}
                                to Enable Add-Ons
                            </>
                        }
                        description={
                            <>
                                Feature add-ons require an active paid plan for all members in the workspace. Learn more
                                about how we handle billing in <OurDocsLink />
                            </>
                        }
                    />
                    <Divider style={{ margin: '1rem 0' }} />
                </>
            )}
            {featureAddOns?.map((fao) => {
                return <FeatureAddOnCard key={fao?.key} fao={fao} />;
            })}
            <Divider style={{ margin: '1rem 0' }} />
            <PlatformAlert
                styles={{ marginBottom: '1rem' }}
                status='info'
                message={
                    <>
                        Pricing is based on your billing cycle preference for{' '}
                        <Link to={'/settings/account/plans/seats'}>
                            Seats
                            <TbArrowRight style={{ verticalAlign: '-0.175rem', marginLeft: '0.125rem' }} />
                        </Link>
                    </>
                }
                description={
                    <>
                        Annual or monthly pricing for Add-Ons are dependent upon your billing cycle for Seats. If you'd
                        like to get different pricing on feature add-ons, you will need to change your billing period
                        for your existing plan.
                    </>
                }
            />
        </div>
    );
};

export default FeatureAddOns;
